import { useMemo } from 'react'
import { Ban, Pencil } from 'lucide-react'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import { useNavigate } from 'react-router'

import { defaultColumnOptions, type TableAny } from '@/utils/tables'
import Flex from '@/elements/Flex'
import Avatar from '@/elements/Avatar'
import Text from '@/elements/Text'
import useRoles from '@/hooks/useRoles'
import type { OrganizationMemberResponse, OrganizationMemberRolesType } from '@/api/types/Organization'
import Pill from '@/elements/Pill'
import PopOver from '@/elements/PopOver'
import { getUserLocation } from '@/utils/location'

export interface OrganizationMemberTableType {
  id: OrganizationMemberResponse['id']
  name: OrganizationMemberResponse['details']['fullName']
  email: OrganizationMemberResponse['email']
  location: string
  role: OrganizationMemberResponse['role']
  avatarImg?: string
  organizationMemberId: OrganizationMemberResponse['organizationMemberId']
  isCognitoConfirmed: boolean
}

const columnHelper = createColumnHelper<OrganizationMemberTableType>()

export function useOrganizationMembersTable(
  members: OrganizationMemberResponse[],
  onDeactivate: (id: string) => void,
) {
  const navigate = useNavigate()
  const { getRoleName, getRoleColor } = useRoles()

  const data: OrganizationMemberTableType[] = useMemo(() => members.map(row => ({
    id: row.id,
    name: row.details?.fullName ?? '',
    email: row.email,
    location: getUserLocation(row.details),
    role: row.role,
    avatarImg: row.assets?.PROFILE_PICTURE || undefined,
    organizationMemberId: row.organizationMemberId,
    isCognitoConfirmed: row.isCognitoConfirmed ?? false,
  })), [members])

  const columns: ColumnDef<OrganizationMemberTableType, TableAny>[] = [
    columnHelper.accessor('name', {
      ...defaultColumnOptions(),
      header: 'Name',
      enableSorting: false,
      cell: info => {
        return (
          <Flex inline align='center' gap={2} className='user-cell'>
            <Avatar
              name={info.getValue() as string}
              src={info.row.original.avatarImg}
              size='extraSmall'
              isMember
            />
            <Text variant='textblock'>{info.getValue() as string}</Text>
          </Flex>
        )
      },
    }),
    columnHelper.accessor('email', {
      ...defaultColumnOptions(),
      header: 'Email',
      meta: { width: 250 },
      enableSorting: false,
    }),
    columnHelper.accessor('location', {
      ...defaultColumnOptions(),
      header: 'Location',
      enableSorting: false,
    }),
    columnHelper.accessor('role', {
      ...defaultColumnOptions(),
      header: 'Role',
      enableSorting: false,
      cell: info => {
        const role = info.getValue() as OrganizationMemberRolesType
        return (
          <Pill customColor={getRoleColor(role)}>
            {getRoleName(role)}
          </Pill>
        )
      }
    }),
    columnHelper.display({
      id: 'actions',
      ...defaultColumnOptions(),
      header: 'Actions',
      meta: { width: 50 },
      enableSorting: false,
      cell: info => {
        const orgMemberId = info.row.original.organizationMemberId.id
        return (
          <Flex justify='center' align='center' className='actions-cell'>
            <PopOver
              position='left'
              options={[
                {
                  label: 'Edit Settings',
                  action: () => navigate(`/member/${orgMemberId}`),
                  icon: Pencil,
                },
                {
                  label: 'Deactivate User',
                  action: () => onDeactivate(orgMemberId),
                  icon: Ban,
                  isDanger: true,
                },
              ]}
            />
          </Flex>
        )
      }
    }),
  ]

  return { data, columns }
}
